/**
 * Uploads a user-provided custom node icon (SVG/PNG/…) to R2 via the backend and
 * returns its object key (+ current public URL). The KEY is stored on the node's
 * `icon` field and resolved to a URL at render time (see lib/assets.ts), so a
 * public-domain change never breaks saved diagrams. Rendered through <img>
 * (which cannot execute SVG scripts). Requires an authenticated session.
 */

export interface IconUploadResult {
  success: boolean;
  /** R2 object key — this is what should be stored, not the absolute URL. */
  key?: string;
  imageUrl?: string;
  error?: string;
}

const ACCEPTED_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE = 1024 * 1024; // 1MB — matches the backend

function apiBase(): string {
  return import.meta.env.VITE_API_URL || 'http://localhost:4000';
}

export async function uploadCustomIcon(file: File): Promise<IconUploadResult> {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return { success: false, error: 'Use an SVG, PNG, JPEG, WebP, or GIF.' };
  }
  if (file.size > MAX_SIZE) {
    return { success: false, error: 'Icon must be under 1MB.' };
  }

  try {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${apiBase()}/api/upload/icon`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return {
        success: false,
        error:
          err.error ||
          (res.status === 401 ? 'Sign in to upload a custom icon.' : 'Upload failed.'),
      };
    }

    const data = await res.json();
    return { success: true, key: data.key, imageUrl: data.imageUrl };
  } catch {
    return { success: false, error: 'Could not reach the server.' };
  }
}

/** Upload raw SVG markup (from a paste) as an .svg file. */
export async function uploadSvgMarkup(svg: string): Promise<IconUploadResult> {
  const trimmed = svg.trim();
  if (!/<svg[\s>]/i.test(trimmed)) {
    return { success: false, error: "That doesn't look like SVG markup." };
  }
  const file = new File([trimmed], 'icon.svg', { type: 'image/svg+xml' });
  return uploadCustomIcon(file);
}
